import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import { apiClient } from '@/lib/apiClient'

interface FormTemplateField {
  name: string
  type: string
  label: string
  required?: boolean
}

interface FormTemplate {
  slug: string
  displayName: string
  verticalCode: string
  fields: FormTemplateField[]
}

export function FormTemplatesPage() {
  const { t } = useTranslation()
  const [templates, setTemplates] = useState<FormTemplate[]>([])
  const [loadError, setLoadError] = useState(false)
  const [preview, setPreview] = useState<FormTemplate | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get<FormTemplate[]>('/form-templates')
      setTemplates(res.data)
    } catch {
      setLoadError(true)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (loadError) {
    return <p className="text-red-600 p-6">{t('formTemplates.error_load')}</p>
  }

  return (
    <div className="space-y-6 p-6">
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-lg rounded-xl border bg-card p-6 shadow-xl space-y-3 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{preview.displayName}</h3>
              <button
                className="text-muted-foreground hover:text-muted-foreground text-xl leading-none"
                onClick={() => setPreview(null)}
              >
                &times;
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t(`vertical.verticals.${preview.verticalCode}` as Parameters<typeof t>[0], {
                defaultValue: preview.verticalCode,
              })}
            </p>
            <ul className="space-y-1">
              {preview.fields.map(f => (
                <li key={f.name} className="text-sm flex items-center gap-2">
                  <span className="font-medium">{f.label}</span>
                  <span className="text-xs text-muted-foreground">{f.type}</span>
                  {f.required && <span className="text-xs text-red-500">*</span>}
                </li>
              ))}
            </ul>
            <button
              className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
              onClick={() => setPreview(null)}
            >
              {t('formTemplates.use_template')}
            </button>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-2xl font-semibold">{t('formTemplates.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('formTemplates.subtitle')}</p>
      </div>

      {templates.length === 0 ? (
        <p className="text-muted-foreground">{t('formTemplates.empty')}</p>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-card border-b">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-foreground">
                  {t('formTemplates.col_name')}
                </th>
                <th className="text-left px-4 py-2 font-medium text-foreground">
                  {t('formTemplates.col_vertical')}
                </th>
                <th className="text-left px-4 py-2 font-medium text-foreground">
                  {t('formTemplates.col_fields')}
                </th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {templates.map(tpl => (
                <tr key={tpl.slug} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium">{tpl.displayName}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {t(`vertical.verticals.${tpl.verticalCode}` as Parameters<typeof t>[0], {
                      defaultValue: tpl.verticalCode,
                    })}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{tpl.fields.length}</td>
                  <td className="px-4 py-2">
                    <button
                      className="text-xs border px-3 py-1 rounded hover:bg-card"
                      onClick={() => setPreview(tpl)}
                    >
                      {t('formTemplates.preview')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
